import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import { Stethoscope, CheckCircle, AlertCircle, ArrowRight, Lock, Eye, EyeOff, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { useSiteSettings } from '../SiteSettingsContext';

const InviteAcceptPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { logoUrl, orgName } = useSiteSettings();
  const { checkAuth } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/invites/${token}`)
      .then(res => { setInvite(res.data); setName(res.data.name || ''); })
      .catch(e => setError(e.response?.data?.detail || 'Invalid or expired invitation'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSubmitting(true);
    try {
      await api.post(`/invites/${token}/accept`, { email: invite.email, password, name });
      toast.success('Account created! Welcome to Guatemala Surgery Mission.');
      await checkAuth();
      navigate('/dashboard', { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-amber-50 flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-amber-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            {logoUrl ? (
              <img src={logoUrl} alt={orgName} className="h-12 w-auto object-contain" />
            ) : (
              <>
                <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                  <Stethoscope className="w-7 h-7 text-white" />
                </div>
                <span className="font-heading font-bold text-2xl text-slate-800">{orgName || 'Guatemala Surgery'}</span>
              </>
            )}
          </Link>
        </div>

        {error ? (
          <Card className="border-0 shadow-xl rounded-2xl text-center">
            <CardContent className="p-10">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-heading font-bold text-slate-800 mb-2">Invitation Invalid</h2>
              <p className="text-slate-500 mb-6">{error}</p>
              <Link to="/login">
                <Button className="rounded-full bg-primary">Go to Sign In</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-xl rounded-2xl">
            <CardHeader className="text-center pb-2 pt-8">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <CardTitle className="text-2xl font-heading font-bold text-slate-800">
                You're Invited!
              </CardTitle>
              <CardDescription className="text-slate-500">
                Create your account to access the portal
                {invite?.user_type === 'staff' && <span className="block text-xs mt-1 text-primary font-medium">Staff Account</span>}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-8 pt-4">
              {/* Pre-filled email */}
              <div className="bg-primary/5 rounded-xl px-4 py-3 mb-6 text-sm text-slate-700">
                Invitation for: <strong>{invite?.email}</strong>
              </div>

              <form noValidate onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-700">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your full name"
                      className="pl-10 h-12 rounded-xl" required data-testid="name-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700">Create Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                      placeholder="At least 6 characters" className="pl-10 pr-10 h-12 rounded-xl" required data-testid="password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input type={showPassword ? 'text' : 'password'} value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      placeholder="Repeat password" className="pl-10 h-12 rounded-xl" required data-testid="confirm-password-input" />
                  </div>
                </div>
                <Button type="submit" className="w-full h-12 rounded-xl bg-rojo hover:bg-rojo-dark font-medium" disabled={submitting} data-testid="accept-invite-btn">
                  {submitting ? 'Creating Account...' : 'Create Account & Sign In'}
                  {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </form>

              <p className="text-center text-xs text-slate-400 mt-4">
                Already have an account?{' '}
                <Link to="/login" className="text-primary hover:underline">Sign in</Link>
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default InviteAcceptPage;
